import {Bundle, IBundle} from "data/BundleData";
import {IResDay} from "shared/ResDay";
import {API} from "api";
import {IReservation} from "shared/IReservation";
import {action, computed, observable} from "mobx";
import {User} from "data/entities/User";
import {IResTime} from "shared/ResTime";
import {DateTime} from "luxon";
import {DateTimeFormat, DateTimeHelpers} from 'shared/DateTimeHelpers';
import {StringHelpers} from "shared/StringHelpers";

interface IReserveSlotResult {
  reservation:IReservation|null;
  bundle:IBundle;
}

export const FAILURE_REASON_SPOT_TAKEN = "FAILURE_REASON_SPOT_TAKEN";

export class ResData {

  @observable private bundle:Bundle;
  @observable private solutionId:string;
  @observable private loadingReservations = false;
  @observable private _reservations:IReservation[]|null;
  @observable private _currentDay:IResDay;

  // updating this value triggers all computed fields that rely on
  // the current date and time to recalculate.
  @observable private now = DateTime.now();

  @computed get resOptions() {
    return this.bundle.resOptions;
  }

  @computed get hasTerms() {
    return this.resOptions && null!==StringHelpers.safeHTML(this.resOptions.terms);
  }

  @computed get fullName() {
    return this.bundle.fullName;
  }

  /**
   * ResDays that have an open registration window
   */
  @computed get resDaysOpenForRegistration() {
    return this.bundle.resDays.filter((resDay)=>{
      const closeDate = DateTimeHelpers.parseDateTime(resDay.closeDate);
      const openDate = DateTimeHelpers.parseDateTime(resDay.openDate);
      return (null!=closeDate && null!=openDate) ? (this.now < closeDate && this.now >= openDate) : false;
    });
  }

  /**
   * Get the furthest close date for res days that are open for registration
   *
   * If there is nothing with a registration window, then 1/1/2000 is returned
   */
  @computed get furthestCloseDate() {
    let furthestCloseDate = DateTimeHelpers.y2k();
    try {
      //const now = DateTime.now();
      // ResDays are always in chronological order
      for (let i = this.resDaysOpenForRegistration.length - 1; i >= 0; i--) {
        const resDay = this.resDaysOpenForRegistration[i];
        const openDateTime = DateTimeHelpers.parseDateTime(resDay.openDate); // DateTime.fromISO(resDay.openDate);
        if (null!=openDateTime && openDateTime <= this.now) {
          const _furthestCloseDate = DateTimeHelpers.parseDateTime(resDay.closeDate);
          if(null!=_furthestCloseDate) {
            furthestCloseDate = _furthestCloseDate;
            break;
          }
        }
      }
    } catch(reason) {
      console.log(reason);
      furthestCloseDate = DateTimeHelpers.y2k();
    }
    return furthestCloseDate;
  }

  @computed get reservationsOpen() {
    return this.furthestCloseDate > DateTime.now();
  }

  @computed get isMultiDay() {
    return this.resDaysOpenForRegistration.length > 1;
  }

  @computed get isMultiTime() {
    if(this.isMultiDay) {
      return true;
    } else if(0 === this.resDaysOpenForRegistration.length) {
      return false;
    } else {
      return this.allAvailableSlots.length > 1;
    }
  }

  @computed get allAvailableSlots() {
    let resTimes:{[id: string]:IResTime} = {};
    for(let rd of this.resDaysOpenForRegistration){
      for(let rdr of rd.resDayResources) {
        for(let rt of rdr.resTimes) {
          if(rt.time) {
            const key = DateTimeHelpers.format(rt.time, DateTimeFormat.DAY_TIME_SERIAL, this.resOptions.timezone)
            if(null!==key) {
              resTimes[key] = rt;
            }
          }
        }
      }
    }
    return Object.keys(resTimes);
  }

  @computed get noSlotsAvailable() {
    for(let rd of this.resDaysOpenForRegistration) {
      for (let rdr of rd.resDayResources) {
        for (let rt of rdr.resTimes) {
          if (rt.time && rt.count < rt.max) {
            return false;
          }
        }
      }
    }
    return true;
  }

  @computed get uiPhraseSlotDescription() {
    return this.bundle.resOptions.slotLength + " Minute "+this.bundle.resOptions.uiWordEventType;
  }

  @computed get reservations() {
    if(this._reservations){
      DateTimeHelpers.sortObjectsByProperty(this._reservations, "time");
    }
    return this._reservations ? this._reservations : [];
  }

  @computed get reservationsNewestFirst() {
    const rev:IReservation[] = [];
    for(let i=this.reservations.length; i>0;i--) {
      rev.push(this.reservations[i-1]);
    }
    return rev;
  }

  @computed get futureReservations() {
    const now = DateTime.now();
    return this.reservations.filter(r=>{
      const timeAsDateTime = DateTimeHelpers.parseDateTime(r.time);
      if(null!==timeAsDateTime) {
        return timeAsDateTime > now;
      }
      return false;
    });
  }


  @computed get currentDay() {
    return this._currentDay;
  }

  @computed get previousDay():IResDay|null {
    if(this.isMultiDay) {
      const idx = this.resDaysOpenForRegistration.findIndex(rd=>(rd._id===this._currentDay._id));
      return idx>0 ? this.resDaysOpenForRegistration[idx-1] : null;
    }
    return null;
  }

  @action goToPreviousDay = () => {
    if(null!=this.previousDay) {
      this.goToDay(this.previousDay);
    }
  }

  @computed get nextDay():IResDay|null {
    if(this.isMultiDay) {
      const idx = this.resDaysOpenForRegistration.findIndex(rd=>(rd._id===this._currentDay._id));
      return idx<this.resDaysOpenForRegistration.length-1 ? this.resDaysOpenForRegistration[idx+1] : null;
    }
    return null;
  }

  @action goToNextDay = () => {
    if(null!=this.nextDay) {
      this.goToDay(this.nextDay);
    }
  }

  @action goToDay = (resDay:IResDay) => {
    this._currentDay = resDay;
  }

  @computed get loadReservationsFailed() {
    return null===this._reservations && !this.loadingReservations;
  }

  @computed get what() {
    return this.bundle.resOptions.slotLength+" minute "+this.bundle.resOptions.uiWordEventType;
  }

  @action init = (bundle:Bundle, user:User, solutionId:string) => new Promise<void>((resolve, reject)=>{
    if(this.loadingReservations) {
      reject("Already loading reservations");
    } else if(!solutionId) {
      reject("Solution ID is required");
    } else {
      this.now = DateTime.now();
      this.bundle = bundle;
      this.loadingReservations = true;
      this.solutionId = solutionId;
      API.get(`/reservation/${this.bundle._id}/reservation`)
        .then(action((reservations: IReservation[]) => {
          this._reservations = reservations;
          this.loadingReservations = false;
          resolve();
        }))
        .catch(action((reason) => {
          console.log(reason);
          this._reservations = [];
          this.loadingReservations = false;
          reject(reason);
        }));
      }
  });


  static availableSlotsForResDay(resDay:IResDay) {
      // get every available slot
      const slots:DateTime[] = [];
      for(let resDayResource of resDay.resDayResources) {
        for(let resTime of resDayResource.resTimes) {
          if(resTime.count<resTime.max) {
            const slot = DateTimeHelpers.parseDateTime(resTime.time);
            if(null!==slot) {
              slots.push(slot);
            }
          }
        }
      }

      // sort by date
      DateTimeHelpers.sortDateTimes(slots);

      // De-duplicate
      let prevSlot = slots[slots.length-1];
      for(let i=slots.length-2;i>-1;i--) {
        // hasSame takes a unit, and compares whether two timestamps match down to that unit
        if(prevSlot.hasSame(slots[i], 'minute')) {
          slots.splice(i,1);
        } else {
          prevSlot = slots[i];
        }
      }

      return slots;
  }

  dayForSlotDateTime = (slot:DateTime) => {
    if(1===this.bundle.resDays.length) {
      return this.bundle.resDays[0];
    } else {
      for(let day of this.bundle.resDays) {
        const resDayDateTime = DateTimeHelpers.parseDateTime(day.day);
        if(null!==resDayDateTime && slot.hasSame(resDayDateTime, 'day')) {
          return day;
        }
      }
    }
    return null;
  }

  cancelReservation = (reservation:IReservation) => new Promise((resolve,reject)=>{
    API.delete(`/reservation/${this.bundle._id}/reservation/${reservation._id}`)
      .then(action((result)=>{

        // remove the reservation from the array
        if(this._reservations) {
          const idx = this._reservations.findIndex(r=>r._id===reservation._id);
          if(-1!==idx) {
            this._reservations.splice(idx, 1);
          }
        }

        // update the bundle
        this.bundle.update(result.bundle);
        resolve(true);
      }))
      .catch((reason)=>{
        reject(reason);
      });

  });



  /**
   * Attempt to reserve a spot. Send back a reservation
   * if successful, and also a bundle with up to date
   * resDays and a resResources
   *
   * {
   *   reservation:null|IReservation,
   *   bundle:IBundle,
   * }
   *
   * @param resDay
   * @param slot
   * @param reservationToReschedule
   */
  reserveSlot = (resDay:IResDay, slot:DateTime, reservationToReschedule?:IReservation):Promise<any> => new Promise((resolve, reject)=>{

    let data = {
      resDayId:resDay._id,
      slotTime:slot.toJSDate(),
      reservationToRescheduleId:reservationToReschedule?reservationToReschedule._id:null,
      solutionId:this.solutionId,
    }

    API.post(`/reservation/${this.bundle._id}/reservation`, data)
      .then((result:IReserveSlotResult)=>{
        this.bundle.update(result.bundle);
        if(result.reservation) {
          if(this._reservations) {
            const reservations = this._reservations;
            if(reservationToReschedule) {
              const idx = reservations.findIndex(r=>r._id===reservationToReschedule._id);
              if(-1!==idx) {
                reservations.splice(idx,1);
              }
            }
            this._reservations =  [...reservations, result.reservation ];
          } else {
            this._reservations = [result.reservation];
          }
          resolve(true);
        } else {
          reject(FAILURE_REASON_SPOT_TAKEN);
        }

      })
      .catch((reason)=>{
        reject(reason)
      });

  });





  // constructor(bundle:Bundle, user:User) {
  //   this.bundle = bundle;
  //   this.loadExistingReservations(user);
  // }

}

export const ResDataFactory = (bundle:Bundle, user:User, solutionId:string) => new Promise((resolve, reject)=>{
  const resData = new ResData();
  resData.init(bundle, user, solutionId)
    .then(()=>{
      resolve(resData)
    })
    .catch((reason)=>{
      reject(reason);
    });
});